'use client';

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faShare,
  faUserPlus,
  faTrash,
  faEllipsisVertical,
} from '@fortawesome/pro-solid-svg-icons';
import useLists from 'src/hooks/lists/useLists';

const ListItemDropdown = ({ listId }): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const {
    setListToManage,
    setShowDeleteListDialog,
    setShowShareListDialog,
    setShowReassignListDialog,
  } = useLists();

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const getDropdownPosition = () => {
    if (!containerRef.current) return { top: 0, right: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    };
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setListToManage(listId);
    setShowShareListDialog(true);
    setOpen(false);
  };

  const handleAssign = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setListToManage(listId);
    setShowReassignListDialog(true);
    setOpen(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setListToManage(listId);
    setShowDeleteListDialog(true);
    setOpen(false);
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <button
        type="button"
        className="w-6 h-6 flex items-center justify-center rounded-full 
        bg-neutral-500/10 text-neutral-600 cursor-pointer
        hover:bg-neutral-500/20 transition-all duration-300"
        onClick={handleToggle}
        onMouseDown={stopPropagation}
        data-testid="list-item-dropdown"
        aria-label="List actions"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Icon icon={faEllipsisVertical} className="text-xs" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={getDropdownPosition()}
            className="fixed bg-white border border-neutral-200 shadow-lg 
            rounded-lg z-[9999] w-48 overflow-hidden py-2"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            <button
              onClick={handleShare}
              onMouseDown={stopPropagation}
              className="w-full px-4 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2 text-neutral-700"
              data-testid="list-item-dropdown-Share"
            >
              <Icon icon={faShare} className="text-neutral-500" />
              Share
            </button>
            <button
              onClick={handleAssign}
              onMouseDown={stopPropagation}
              className="w-full px-4 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2 text-neutral-700"
              data-testid="list-item-dropdown-Assign"
            >
              <Icon icon={faUserPlus} className="text-neutral-500" />
              Assign
            </button>
            <button
              onClick={handleDelete}
              onMouseDown={stopPropagation}
              className="w-full px-4 py-2 text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
              data-testid="list-item-dropdown-Delete"
            >
              <Icon icon={faTrash} className="text-red-500" />
              Delete
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export { ListItemDropdown as default };
